import React, { useState } from "react";
import { useAuth } from "../../AuthProvider";
import { useUser } from "../../UserContext";

import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Card
} from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import OtpInput from "react-otp-input";
import { toast } from "react-toastify";
import IconHeader from "../../images/logo.png";
import { AiOutlineEdit } from "react-icons/ai";
import "./style.scss";
import axios from "axios";
import AppConstants from "../../AppConstants";

function TwoFactorOTP() {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitCooldown, setSubmitCooldown] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  const { login } = useAuth();
  const { setUser } = useUser();

  const handleOtpChange = (otpValue: string) => {
    if (/^\d{0,6}$/.test(otpValue)) {
      setOtp(otpValue);
    }
  };

  // Helper function to start a cooldown timer
  const startCooldown = (
    setCooldown: React.Dispatch<React.SetStateAction<number>>,
    seconds = 15
  ) => {
    setCooldown(seconds);
    const interval = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;

    if (!email) {
      toast.error("Email is missing. Cannot resend OTP.", {
        position: toast.POSITION.TOP_RIGHT
      });
      return;
    }

    try {
      startCooldown(setResendCooldown);
      setLoading(true);

      const response = await axios.post(
        AppConstants.ADMIN_RESEND_OTP_API,
        { email },
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

      if (response.status === 200) {
        const message =
          response?.data?.message ||
          "OTP resent successfully! Check your email.";
        toast.success(message, {
          position: toast.POSITION.TOP_RIGHT
        });
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.error ||
        error.message ||
        "Failed to resend OTP. Please try again.";

      toast.error(message, {
        position: toast.POSITION.TOP_RIGHT
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Check if OTP is empty
    if (!otp.trim()) {
      toast.error("Please enter OTP", {
        position: toast.POSITION.TOP_RIGHT
      });
      return;
    }
    // Prevent multiple submit clicks during cooldown
    if (submitCooldown > 0) return;

    startCooldown(setSubmitCooldown);
    setLoading(true);

    try {
      const response = await axios.post(
        AppConstants.ADMIN_VERIFY_OTP_API,
        {
          email,
          otp
        },
        {
          headers: {
            "Content-Type": "application/json",
            "User-Agent": navigator.userAgent
          }
        }
      );

      if (response.status === 200) {
        console.log(response.data, "data------");
        const { token, user } = response.data;

        login(token, {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          profileImage: user.profileImage // if available
        });
        
        setUser(user); 
        localStorage.setItem("authUser", JSON.stringify(user)); 

        toast.success("OTP verified successfully!", {
          position: toast.POSITION.TOP_RIGHT
        });

        navigate("/dashboard", { replace: true });
      }
    } catch (error: any) {
      console.log(error, "error");
      const message =
        error?.response?.data?.error ||
        error.message ||
        "OTP verification failed";

      toast.error(message, {
        position: toast.POSITION.TOP_RIGHT
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="dashboard credential-page">
      <Container fluid>
        <Row className="justify-content-md-center">
          <Col xl={4} lg={6} sm={12}>
            <Card className="px-5 pt-4 pb-5">
              <figure className="text-center">
                <img src={IconHeader} alt="logo" width={250} />
                <h2>One Time Passcode</h2>
                <p className="mb-2">We have sent the code verification to</p>
                <h5>{email} </h5>
              </figure>

              <Form onSubmit={handleSubmit} className="otp-screen">
                <div className="d-flex justify-content-center mb-2">
                  <OtpInput
                    value={otp}
                    onChange={handleOtpChange}
                    numInputs={6}
                    shouldAutoFocus
                    containerStyle="otp-container"
                    inputStyle={{
                      background: "#fff",
                      height: "47px",
                      width: "50px",
                      fontSize: "22px",
                      color: "#383838",
                      fontWeight: "700",
                      caretColor: "blue",
                      textAlign: "center",
                      marginRight: "5px"
                    }}
                    renderInput={(props) => <input {...props} />}
                  />
                </div>
                <Button
                  variant="primary"
                  className="mt-2 border-0 text-white font-16 w-100 py-3 gilroy-semibold"
                  type="submit"
                  disabled={loading || submitCooldown > 0}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Verifying...
                    </>
                  ) : submitCooldown > 0 ? (
                    `Verify (${submitCooldown}s)`
                  ) : (
                    "Verify"
                  )}
                </Button>

                <h5 className="mt-4 text-center text-light">
                  Haven't received One Time Passcode? <br />
                  <span
                    className="text-primary fw-bold"
                    style={{
                      cursor: resendCooldown > 0 ? "not-allowed" : "pointer",
                      color: resendCooldown > 0 ? "#888" : "inherit"
                    }}
                    onClick={resendCooldown > 0 ? undefined : handleResendOTP}
                  >
                    {resendCooldown > 0
                      ? `Resend (${resendCooldown}s)`
                      : "Resend"}
                  </span>
                </h5>
              </Form>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
}

export default TwoFactorOTP;
